/**
 * Canonical base URL for the deployed app.
 *
 * Used in places that MUST NOT depend on window.location.origin —
 * specifically, Supabase Auth `redirectTo` parameters that get baked
 * into emailed reset / confirmation / OAuth-return links. If a user
 * triggers forgot-password while running localhost dev, the email
 * should still point to production (the env vars decide).
 *
 * Resolution order:
 *   1. import.meta.env.VITE_APP_URL — set in Vercel/Netlify for prod
 *      and preview deploys. This is the authoritative source.
 *   2. window.location.origin — only used when VITE_APP_URL is unset,
 *      which is the case for unconfigured local dev. Keeps localhost
 *      end-to-end testing working without forcing a separate env file.
 *   3. https://getpulsemd.com — SSR / build-time fallback when window
 *      is not defined.
 *
 * **Production deploys MUST set VITE_APP_URL=https://getpulsemd.com**
 * in their host env. Otherwise production users get whatever origin
 * the build happened to be running at (Vercel's preview URL, etc.),
 * which is exactly the bug we're fixing here.
 */
export const SITE_URL: string =
  import.meta.env.VITE_APP_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://getpulsemd.com');

/**
 * Build an absolute URL by joining SITE_URL with a path. Leading slash
 * on `path` is optional.
 */
export function buildSiteUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${cleanPath}`;
}
